import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Check, Circle, ArrowRight, Sparkles } from "lucide-react";

export default function SetupProgress() {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/summary"),
      api.get("/heirs"),
      api.get("/vault"),
    ])
      .then(([sum, heirs, vault]) => {
        const heirsWithName = heirs.data;
        const itemsAssigned = vault.data.some((v) => (v.assigned_heirs || []).length > 0);
        const s = sum.data;
        const steps = [
          { k: "vault", done: s.vault_count > 0, to: "/vault", label: t("setup_step_vault") },
          { k: "contact", done: s.contacts_count > 0, to: "/proof-of-life#contacts", label: t("setup_step_contact") },
          { k: "msg", done: s.messages_count > 0, to: "/messages", label: t("setup_step_msg") },
          { k: "heir", done: s.heirs_count > 0, to: "/heirs", label: t("setup_step_heir") },
          { k: "assign", done: heirsWithName.length > 0 && itemsAssigned, to: "/vault", label: t("setup_step_assign") },
        ];
        setData({ steps, percent: Math.round((steps.filter((s) => s.done).length / steps.length) * 100) });
      })
      .catch(() => setData(null));
  }, [t]);

  if (!data) return null;
  if (data.percent === 100) return null;

  const nextStep = data.steps.find((s) => !s.done);

  return (
    <div className="glass-card rounded-md p-6 md:p-7 mb-10" data-testid="setup-progress">
      <div className="flex items-start gap-5 mb-5">
        <Sparkles className="w-6 h-6 text-gold shrink-0 mt-1" />
        <div className="flex-1">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <div className="font-label text-gold">{t("setup_title")}</div>
              <div className="font-display text-3xl text-gold-light">
                {data.percent}% {t("setup_complete")}
              </div>
            </div>
            {nextStep && (
              <Link to={nextStep.to} className="text-sm text-gold hover:text-gold-light flex items-center gap-2" data-testid="setup-next-step">
                {nextStep.label} <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </div>
          <div className="h-1.5 bg-obsidian-2 rounded mt-4 overflow-hidden">
            <div
              className="h-full bg-gold transition-all duration-700"
              style={{ width: `${data.percent}%` }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
        {data.steps.map((s) => (
          <Link
            key={s.k}
            to={s.to}
            className={`flex items-center gap-3 text-xs px-3 py-2 rounded transition ${
              s.done ? "text-gold-light/70" : "text-muted hover:text-gold-light"
            }`}
            data-testid={`setup-step-${s.k}`}
          >
            {s.done ? (
              <Check className="w-4 h-4 text-gold shrink-0" />
            ) : (
              <Circle className="w-4 h-4 text-faded shrink-0" />
            )}
            <span className={s.done ? "line-through opacity-60" : ""}>{s.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
